import { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import CommonInvoiceTemplate from '../../../../documents/components/CommonInvoiceTemplate'
import { SingleDebitNoteData, TableColumn } from '../../../../types'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { request } from '../../../../../../utils/request'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import PrintButtons from '../../../../../../components/PrintButtons'
import usePrint from '../../../../../../utils/usePrint'
import PrintableContainer from '../../../../../../components/PrintableContainer'
import { BusinessInfo, TaxInfo } from '../../../../../controlPanel/types'

const ShowDebitNote = () => {
  const [loading, setLoading] = useState(true);
  const [singleDebit, setSingleDebitNote] = useState<SingleDebitNoteData | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const [taxInfo, setTaxInfo] = useState<TaxInfo>();
  const params = useParams()
  const debitNoteId = params.debitNoteId

  const { isPrinting, handlePrint } = usePrint();

  const fetchBusinessInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/businessInfo",
        method: "get",
      });
  
      if (res.success) {
        setBusinessInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
  
      toast.error(err.message || "Failed to fetch business info");
    }
  }, []);
  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo])

  const fetchTaxInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/taxInfo",
        method: "get",
      });
  
      if (res.success) {
        setTaxInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
  
      toast.error(err.message || "Failed to fetch tax info");
    }
  }, []);
  useEffect(() => {
    fetchTaxInfo();
  }, [fetchTaxInfo])

  const fetchSingleDebitNote = useCallback(async () => {
    if (!debitNoteId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `debitNote/get-single-debit-note/${debitNoteId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSingleDebitNote(res.data)
      } else {
        toast.error(res.message || "Failed to fetch Debit note ");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching Debit Note");
    } finally {
      setLoading(false)
    }
  }, [debitNoteId])

  useEffect(() => {
    fetchSingleDebitNote();
  }, [fetchSingleDebitNote])

  // Format date to readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('debit_note_print', {
          title: `Debit Note - ${singleDebit?.debitNoteNumber}'}`,
          fileName: `Debit _${singleDebit?.debitNoteNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  // Calculate totals from invoice items
  const calculateTotals = () => {
    if (!singleDebit?.items?.length) {
      return {
        quantity: 0,
        taxableAmount: 0,
        tax: 0,
        amount: 0
      };
    }

    const totals = singleDebit.items.reduce((acc, item) => {
      acc.quantity += item.quantity || 0;
      acc.amount += item.amount || 0;
      // Calculate tax amount based on item amount and tax rate
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      acc.tax += taxAmount;
      // Taxable amount is amount minus tax (for GST calculations)
      acc.taxableAmount += (item.amount || 0) - taxAmount;
      return acc;
    }, { quantity: 0, taxableAmount: 0, tax: 0, amount: 0 });

    return totals;
  };

  const prepareTableData = () => {
    if (!singleDebit?.items?.length) return [];

    return singleDebit.items.map((item, index) => {
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      return {
        id: item.id || index.toString(),
        item: item.itemName || `Item ${index + 1}`,
        hsnNo: item.hsnNo || 'N/A',
        quantity: item.quantity || 0,
        rate: item.price || 0,
        tax: taxAmount,
        taxPercentage: item.tax?.rate || 0,
        amount: item.amount || 0,
        discount: item.discount?.amount || 0,
        discountPercentage: item.discount?.isPercentage ? item.discount.amount : 0
      };
    });
  };

  // Prepare table columns based on GST type
  const prepareTableColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      // { id: 'srNo', label: 'Sr No', align: "left", width: 60 },
      { id: 'item', label: 'Item Description', align: "left", width: 200 },
      { id: 'quantity', label: 'Qty', align: "right", width: 80 },
      { id: 'rate', label: 'Rate (₹)', align: "right", width: 100 },
      { id: 'discount', label: 'Discount (₹)', align: "right", width: 100 },
    ];

    if (singleDebit?.gstType === 'GST') {
      // baseColumns.push({ id: 'hsnNo', label: 'HSN/SAC', align: "left", width: 100 });
    }

    baseColumns.push(
      // { id: 'taxPercentage', label: 'Tax %', align: "right", width: 80 },
      { id: 'tax', label: 'Tax Amt (₹)', align: "right", width: 100 },
      { id: 'amount', label: 'Amount (₹)', align: "right", width: 120 }
    );

    return baseColumns;
  };

  // Prepare summary items
  const prepareSummaryItems = () => {
    if (!singleDebit) return [];

    const totals = calculateTotals();
    const summaryItems = [];

    // Taxable Amount
    summaryItems.push({
      label: 'Taxable Amount',
      value: singleDebit?.taxableAmount,
      variant: 'body2' as const
    });

    // GST Breakdown if GST Sales Return 
    if (singleDebit.gstType === 'GST' && singleDebit.taxBreakdown?.length > 0) {
      singleDebit.taxBreakdown.forEach((taxItem, index) => {
        if (taxItem.cgst > 0) {
          summaryItems.push({
            label: `CGST (${taxItem.cgst}%)`,
            value: (totals.taxableAmount * taxItem.cgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.sgst > 0) {
          summaryItems.push({
            label: `SGST (${taxItem.sgst}%)`,
            value: (totals.taxableAmount * taxItem.sgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.igst > 0) {
          summaryItems.push({
            label: `IGST (${taxItem.igst}%)`,
            value: (totals.taxableAmount * taxItem.igst) / 100,
            variant: 'body2' as const
          });
        }
      });
    } else if (singleDebit.gstType === 'GST') {
      // Fallback if no tax breakdown
      summaryItems.push({
        label: `GST (${singleDebit.items[0]?.tax?.rate || 0}%)`,
        value: totals.tax,
        variant: 'body2' as const
      });
    }

    // Additional Charges
    if (singleDebit.charges?.length > 0) {
      singleDebit.charges.forEach(charge => {
        summaryItems.push({
          label: charge.label,
          value: charge.amount,
          variant: 'body2' as const
        });
      });
    }

    // Discount
    if (singleDebit.discount?.amount && singleDebit.discount.amount > 0) {
      const discountLabel = singleDebit.discount.isPercentage
        ? `Discount (${singleDebit.discount.amount}%)`
        : 'Discount';
      summaryItems.push({
        label: discountLabel,
        value: singleDebit.discount.amount,
        variant: 'body2' as const
      });
    }

    // Round Off
    if (singleDebit.roundOff) {
      const roundOffValue = Math.round(totals.amount + totals.tax) - (totals.amount + totals.tax);
      if (Math.abs(roundOffValue) > 0) {
        summaryItems.push({
          label: 'Round Off',
          value: roundOffValue,
          variant: 'body2' as const
        });
      }
    }

    // Grand Total
    const grandTotal = singleDebit.totalAmount;
    summaryItems.push({
      label: 'Grand Total',
      value: grandTotal,
      bold: true,
      variant: 'body1' as const
    });

    return summaryItems;
  };

  // Prepare custom fields for payment info
  const prepareCustomFields = () => {
    if (!singleDebit) return [];

    const fields = [];

    if (singleDebit.receivedAmount >= 0) {
      fields.push({
        label: "Paid Amount",
        value: singleDebit.receivedAmount
      });
    }

    if (singleDebit.balanceAmount >= 0) {
      fields.push({
        label: "Balance Due",
        value: singleDebit.balanceAmount
      });
    }

    return fields;
  };

  if (!singleDebit && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <DocumentHeader
          heading='Sales Return'
          subtitle='Sales Return not found'
          showBackButton={true}
        />
      </Box>
    );
  }

  const totals = calculateTotals();
  const tableData = prepareTableData();
  const tableColumns = prepareTableColumns();
  const summaryItems = prepareSummaryItems();
  const customFields = prepareCustomFields();

  return (
    <>
      <DocumentHeader
        heading='Debit Note'
        subtitle={`#${singleDebit?.debitNoteNumber || 'Loading...'}`}
        status={singleDebit?.status || 'UNPAID'}
        statusColor={singleDebit?.status === 'PAID' ? 'success' :
          singleDebit?.status === 'PARTIAL_PAID' ? 'info' : singleDebit?.status === 'UNPAID' ? 'warning' : 'primary'}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>

        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print PR'
          pdfLabel='Export PDF'
          disabled={loading || !singleDebit}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
      </Box>

      <PrintableContainer id="debit_note_print">
        <CommonInvoiceTemplate
          documentType={singleDebit?.debitNoteType || "DEBIT_NOTE"}
          title={singleDebit?.gstType === 'GST' ? "DEBIT NOTE" : "DEBIT NOTE"}
         
          companyName={businessInfo?.businessName}
          companyAddress={
            (businessInfo?.address) ? (`${businessInfo.address?.[0]?.line1} ${businessInfo.address?.[0]?.city} ${businessInfo.address?.[0]?.state}  ${businessInfo.address?.[0]?.pinCode}`) : 'Address not mentioned'
          }
          companyPhone={(businessInfo?.contact?.phone?.[0]?.phoneNo) ? (`${businessInfo?.contact?.phone?.[0]?.phoneNo}`) : '+91 0000000000'}
          companyGSTIN={singleDebit?.gstType === 'GST' ? taxInfo?.taxDetails?.gstNumber : "" }
          
          documentNumber={singleDebit?.debitNoteNumber || ''}
          documentDate={formatDate(singleDebit?.debitNoteDate || singleDebit?.date || '')}
          dueDate={formatDate(singleDebit?.dueDate || '')}
          partyHeading='Vendor/Party Info.'
          partyName={singleDebit?.party?.partyName || singleDebit?.vendor?.vendorName || 'N/A'}
          partyLocation={singleDebit?.party?.location || singleDebit?.vendor?.location}
          partyAddress={
            (singleDebit?.party?.address) ? (`${singleDebit.party.address.line1} ${singleDebit.party.address.city} ${singleDebit.party.address.state}  ${singleDebit.party.address.pinCode}`) : (singleDebit?.vendor?.address) ? (`${singleDebit.vendor.address.line1} ${singleDebit.vendor.address.city} ${singleDebit.vendor.address.state}  ${singleDebit.vendor.address.pinCode}`) : 'Address not available'
          }
          partyPhone={singleDebit?.party?.contact?.phone?.[0]?.phoneNo || singleDebit?.vendor?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          partyGSTIN={singleDebit?.party?.gstNumber || singleDebit?.vendor?.gstNumber || 'N/A'}
          tableColumns={tableColumns}
          tableData={tableData}
          tableTotals={{
            quantity: totals.quantity,
            taxableAmount: totals.taxableAmount,
            tax: totals.tax,
            amount: totals.amount
          }}
          amounts={{
            totalAmount: singleDebit?.totalAmount,
            taxableAmount: singleDebit?.taxableAmount,
            subtotal: singleDebit?.subtotal,
            receivedAmount: singleDebit?.receivedAmount,
            balanceAmount: singleDebit?.balanceAmount
          }}
          summaryItems={summaryItems}
          customFields={customFields}
          showLoading={loading}
          showLoadingText="Fetching Debit Note..."
          // s={singleInvoice?.s || ''}
          termsConditions={singleDebit?.terms || ''}
          gstType={singleDebit?.gstType}
          invoiceStatus={singleDebit?.status}
        />
      </PrintableContainer>
    </>
  )
}

export default ShowDebitNote